import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, X, Crown, ExternalLink, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CollabTool {
  name: string;
  logo: string;
  url: string;
  category: string;
  pricing: string;
  features: {
    realTimeCollab: boolean;
    videoConferencing: boolean;
    fileSharing: boolean;
    taskManagement: boolean;
    whiteboard: boolean;
    integrations: string;
    mobileApp: boolean;
  };
  bestFor: string;
  isRecommended?: boolean;
}

const collabTools: CollabTool[] = [
  {
    name: "Slack",
    logo: "💬",
    url: "https://slack.com",
    category: "Communication",
    pricing: "Free / $7.25/user",
    features: {
      realTimeCollab: true,
      videoConferencing: true,
      fileSharing: true,
      taskManagement: false,
      whiteboard: false,
      integrations: "2400+",
      mobileApp: true,
    },
    bestFor: "Team messaging",
  },
  {
    name: "Microsoft Teams",
    logo: "👥",
    url: "https://microsoft.com/teams",
    category: "Communication",
    pricing: "Free / $4/user",
    features: {
      realTimeCollab: true,
      videoConferencing: true,
      fileSharing: true,
      taskManagement: true,
      whiteboard: true,
      integrations: "700+",
      mobileApp: true,
    },
    bestFor: "Enterprise + Microsoft 365",
  },
  {
    name: "Notion",
    logo: "📝",
    url: "https://notion.so",
    category: "Documentation",
    pricing: "Free / $10/user",
    features: {
      realTimeCollab: true,
      videoConferencing: false,
      fileSharing: true,
      taskManagement: true,
      whiteboard: false,
      integrations: "50+",
      mobileApp: true,
    },
    bestFor: "Docs + knowledge base",
  },
  {
    name: "Miro",
    logo: "🎨",
    url: "https://miro.com",
    category: "Visual Collaboration",
    pricing: "Free / $8/user",
    features: {
      realTimeCollab: true,
      videoConferencing: true,
      fileSharing: true,
      taskManagement: false,
      whiteboard: true,
      integrations: "100+",
      mobileApp: true,
    },
    bestFor: "Visual brainstorming",
  },
  {
    name: "Figma",
    logo: "🎯",
    url: "https://figma.com",
    category: "Design",
    pricing: "Free / $15/user",
    features: {
      realTimeCollab: true,
      videoConferencing: false,
      fileSharing: true,
      taskManagement: false,
      whiteboard: true,
      integrations: "300+",
      mobileApp: true,
    },
    bestFor: "Design collaboration",
  },
  {
    name: "Loom",
    logo: "🎥",
    url: "https://loom.com",
    category: "Async Video",
    pricing: "Free / $12.50/user",
    features: {
      realTimeCollab: false,
      videoConferencing: false,
      fileSharing: true,
      taskManagement: false,
      whiteboard: false,
      integrations: "50+",
      mobileApp: true,
    },
    bestFor: "Async video messages",
  },
  {
    name: "Google Workspace",
    logo: "🔵",
    url: "https://workspace.google.com",
    category: "Productivity Suite",
    pricing: "$6/user",
    features: {
      realTimeCollab: true,
      videoConferencing: true,
      fileSharing: true,
      taskManagement: true,
      whiteboard: true,
      integrations: "300+",
      mobileApp: true,
    },
    bestFor: "All-in-one productivity",
  },
  {
    name: "Zoom",
    logo: "📹",
    url: "https://zoom.us",
    category: "Video",
    pricing: "Free / $15.99/user",
    features: {
      realTimeCollab: true,
      videoConferencing: true,
      fileSharing: true,
      taskManagement: false,
      whiteboard: true,
      integrations: "1500+",
      mobileApp: true,
    },
    bestFor: "Video meetings",
  },
];

const featureLabels = {
  realTimeCollab: "Real-Time Collab",
  videoConferencing: "Video Calls",
  fileSharing: "File Sharing",
  taskManagement: "Tasks",
  whiteboard: "Whiteboard",
  mobileApp: "Mobile App",
};

export const CollaborationComparison = () => {
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = [...new Set(collabTools.map(t => t.category))];

  const filteredTools = collabTools.filter(tool => {
    // Category filter
    if (selectedCategory && tool.category !== selectedCategory) return false;
    
    // Feature filter
    if (selectedFeatures.length > 0) {
      return selectedFeatures.every(feature => 
        tool.features[feature as keyof typeof tool.features] === true
      );
    }
    return true;
  });

  const toggleFeature = (feature: string) => {
    setSelectedFeatures(prev => 
      prev.includes(feature) 
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  const FeatureCell = ({ value }: { value: boolean }) => (
    value 
      ? <Check className="w-4 h-4 text-green-500 mx-auto" />
      : <X className="w-4 h-4 text-muted-foreground/30 mx-auto" />
  );

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <p className="text-muted-foreground">
          Compare team collaboration tools by features, pricing, and use cases. Filter by the features you need.
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Filter className="w-4 h-4 text-primary" />
            Filter by Features
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(featureLabels).map(([key, label]) => (
              <Button
                key={key}
                variant={selectedFeatures.includes(key) ? "default" : "outline"}
                size="sm"
                onClick={() => toggleFeature(key)}
                className="text-xs"
              >
                {label}
              </Button>
            ))}
          </div>
          
          <div className="flex items-center gap-2 text-sm font-medium text-foreground pt-2">
            Category
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === null ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(null)}
              className="text-xs"
            >
              All
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(cat)}
                className="text-xs"
              >
                {cat}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <p className="text-sm text-muted-foreground">
        Showing {filteredTools.length} of {collabTools.length} tools
      </p>

      {/* Comparison Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[150px]">Tool</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Pricing</TableHead>
              <TableHead className="text-center">Real-Time</TableHead>
              <TableHead className="text-center">Video</TableHead>
              <TableHead className="text-center">Tasks</TableHead>
              <TableHead className="text-center">Whiteboard</TableHead>
              <TableHead>Integrations</TableHead>
              <TableHead className="min-w-[120px]">Best For</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTools.map((tool) => (
              <TableRow key={tool.name}>
                <TableCell className="font-medium">
                  <a 
                    href={tool.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:text-primary transition-colors"
                  >
                    <span className="text-lg">{tool.logo}</span>
                    <span>{tool.name}</span>
                    <ExternalLink className="w-3 h-3 opacity-50" />
                  </a>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {tool.category}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">{tool.pricing}</TableCell>
                <TableCell><FeatureCell value={tool.features.realTimeCollab} /></TableCell>
                <TableCell><FeatureCell value={tool.features.videoConferencing} /></TableCell>
                <TableCell><FeatureCell value={tool.features.taskManagement} /></TableCell>
                <TableCell><FeatureCell value={tool.features.whiteboard} /></TableCell>
                <TableCell className="text-sm">{tool.features.integrations}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{tool.bestFor}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {filteredTools.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">No tools match your selected filters.</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                setSelectedFeatures([]);
                setSelectedCategory(null);
              }}
              className="mt-4"
            >
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
